import React, { useRef } from 'react';
import { useDrag } from 'react-dnd';
import { ColumnProps } from 'antd/lib/table';

import { UserDragItem } from '@totem/components/orgManagement/orgDetails/users/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { User } from '@totem/components/users/container/types';
import { getRoleString } from '@totem/components/users/container/utilities';
import { UserOrganization } from '@totem/types/user';
import { isNotNull } from '@totem/utilities/common';
import {
  sortNumberAscending,
  sortStringAscending,
} from '@totem/utilities/tableUtilities';

type Props = {
  user: User;
};

const InternalUserOrgsTable = ({ user }: Props) => {
  const DND_ITEM_TYPE = 'INTERNAL_USER_ORGANIZATION';

  type DragItem = {
    userOrg: UserDragItem;
  };

  type DragRowProps = React.HTMLAttributes<HTMLTableRowElement> & {
    index?: number;
    record?: UserOrganization;
  };

  const DraggableCustomerRow = ({
    index,
    record,
    className,
    style,
    ...restProps
  }: DragRowProps) => {
    const ref = useRef<HTMLTableRowElement>(null);

    const [{ isDragging }, drag] = useDrag({
      type: DND_ITEM_TYPE,
      item: {
        userOrg: {
          user,
          organization: record,
        },
      } as DragItem,
      canDrag: !!record?.id,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    drag(ref);

    return (
      <tr
        {...restProps}
        ref={ref}
        className={className}
        style={{
          cursor: 'grab',
          opacity: isDragging ? 0.4 : 1,
          ...style,
        }}
        data-row-index={index}
      />
    );
  };

  const columns: ColumnProps<UserOrganization>[] = [
    {
      title: 'Organization Name',
      dataIndex: 'name',
      key: 'name',
      render: (_, record: UserOrganization) => <span>{record.name}</span>,
      defaultSortOrder: 'ascend',
      sorter: (compA, compB) => sortStringAscending(compA.name, compB.name),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (_, record: UserOrganization) => (
        <span>{getRoleString(record.role)}</span>
      ),
      defaultSortOrder: 'ascend',
      sorter: (compA, compB) => sortNumberAscending(compA.role, compB.role),
    },
    {
      title: 'Security Groups',
      dataIndex: 'securityGroups',
      key: 'securityGroups',
      render: (_, record: UserOrganization) => {
        return (
          <div>
            {record.securityGroups.map((item) => (
              <div key={item.id}>{item.name}</div>
            ))}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <IBTable
        showSorterTooltip
        columns={columns}
        dataSource={
          isNotNull(user) && isNotNull(user.organizations)
            ? user.organizations
            : []
        }
        loading={false}
        rowKey={(record) => record.id}
        components={{
          body: {
            row: DraggableCustomerRow,
          },
        }}
        onRow={(record, index) =>
          ({
            index,
            record,
          }) as React.HTMLAttributes<HTMLElement>
        }
        pagination={{
          showSizeChanger: true,
          pageSize: 100,
        }}
      />
    </>
  );
};

export default InternalUserOrgsTable;
